// Facade over the merchandising analytics endpoints.
//
// Calls go to the real controller (MerchandisingAnalyticsController). The
// reporting layer was deployed to lhf-movement on 2026-08-28 and the four daily
// aggregates built over 2026-07-01..08-27, so the endpoints answer with data.
//
// The mock branch is kept rather than deleted: it is the only way to open these
// pages without an API behind them, and the payload shapes are already verified
// against the DTOs. Flip USE_MOCK back to true to use it.
//
// NOT yet populated: dw.AggProductAffinity. It is a weekly build and has not
// been run, so products/affinity returns empty -- which the affinity panel must
// read as "not eligible or below support", never as "never bought together".

import api from '../api/api';
import * as mock from './mockData';

export const USE_MOCK = false;

// Mock latency, so loading states are exercised rather than never seen.
const LATENCY_MS = 260;

function resolve(payload) {
  return new Promise((res) => {
    setTimeout(() => res(payload), LATENCY_MS);
  });
}

// The tool's endpoints answer { data, success, message }; unwrap to `data` so
// callers deal in the analytics envelope only.
function unwrap(response) {
  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Request failed');
  }
  return response.data.data;
}

async function post(route, body) {
  return unwrap(await api.post(`analytics/${route}`, body));
}

async function get(route) {
  return unwrap(await api.get(`analytics/${route}`));
}

// The two SERIES endpoints answer with a bare array, not the paged envelope the
// rollup endpoints use: the controller returns `result.Points` where the others
// return `result.Result`. A series has no page, no grand totals and nothing to
// sort, so an envelope around it would be four null fields and a length -- the
// API is right to omit it.
//
// Every page, and every mock, nonetheless reads `.items`. Unwrapped, an array
// has no `.items`, so `rows` came back undefined and the screen rendered its
// empty state with no error anywhere: api/analytics/sales/hourly answered 200,
// the traffic page just showed nothing, and the trend chart on the store
// scorecard went blank next to a table that worked.
//
// Normalising here rather than in the pages keeps the mock and the live call
// interchangeable, which is the whole point of this module. Already-enveloped
// payloads pass through untouched, so this stays correct if the API ever wraps
// them.
const asSeries = (payload) =>
  (Array.isArray(payload)
    ? { total: payload.length, items: payload, totals: null, quality: null }
    : payload);

// --- Sales ------------------------------------------------------------------

export const getSalesHierarchy = (filter) =>
  (USE_MOCK
    ? resolve(mock.salesHierarchy(filter))
    : post('sales/hierarchy', filter));

export const getSalesProduct = (filter) =>
  (USE_MOCK ? resolve(mock.salesProduct(filter)) : post('sales/product', filter));

export const getSalesTrend = (filter) =>
  (USE_MOCK
    ? resolve(mock.salesTrend(filter))
    : post('sales/trend', filter).then(asSeries));

export const getSalesHourly = (filter) =>
  (USE_MOCK
    ? resolve(mock.salesHourly(filter))
    : post('sales/hourly', filter).then(asSeries));

// --- Shrinkage --------------------------------------------------------------

export const getShrinkageSummary = (filter) =>
  (USE_MOCK ? resolve(mock.shrinkageSummary(filter)) : post('shrinkage/summary', filter));

export const getShrinkageVsSales = (filter) =>
  (USE_MOCK ? resolve(mock.shrinkageVsSales(filter)) : post('shrinkage/vs-sales', filter));

export const getShrinkageByEmployee = (filter) =>
  (USE_MOCK ? resolve(mock.shrinkageByEmployee(filter)) : post('shrinkage/by-employee', filter));

// --- Orders -----------------------------------------------------------------

// completedSalesOnly must be false for cancellationRatePct to mean anything --
// leaving it on removes cancelled orders from the denominator and the rate comes
// back a flat zero that looks like good news.
export const getOrderMetrics = (filter) =>
  (USE_MOCK
    ? resolve(mock.orderMetrics({ completedSalesOnly: false, ...filter }))
    : post('orders/metrics', { completedSalesOnly: false, ...filter }));

export const getProductAffinity = (filter) =>
  (USE_MOCK ? resolve(mock.productAffinity(filter)) : post('products/affinity', filter));

// --- Data quality -----------------------------------------------------------

export const getUnmapped = (filter) =>
  (USE_MOCK ? resolve(mock.dqUnmapped(filter)) : post('dq/unmapped', filter));

export const getZeroCost = (filter) =>
  (USE_MOCK ? resolve(mock.dqZeroCost(filter)) : post('dq/zero-cost', filter));

export const getCatalogDrift = (filter) =>
  (USE_MOCK ? resolve(mock.dqCatalogDrift(filter)) : post('dq/catalog-drift', filter));

// --- Reference & meta -------------------------------------------------------

// Bind store filters to this; never hardcode the list.
export const getStores = () =>
  (USE_MOCK ? resolve(mock.referenceStores()) : get('reference/stores'));

// Warehouse load status is not an analytics concern and no longer shows on
// these pages -- see warehouse/warehouseApi.js and the Data Warehouse page.
